import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PaginationProps {
    page: number
    pageSize: number
    total: number
    basePath: string
    params?: Record<string, string | null | undefined>
    anchor?: string
}

const buildHref = (basePath: string, params: Record<string, string | null | undefined>, page: number, anchor?: string): string => {
    const search = new URLSearchParams()

    for (const [key, value] of Object.entries(params)) {
        if (value) search.set(key, value)
    }

    if (page > 1) search.set('page', String(page))

    const query = search.toString()

    return `${basePath}${query ? `?${query}` : ''}${anchor ? `#${anchor}` : ''}`
}

const pageWindow = (page: number, pages: number): (number | 'gap')[] => {
    const result: (number | 'gap')[] = []
    const visible = new Set([1, pages, page - 1, page, page + 1].filter(value => value >= 1 && value <= pages))
    const sorted = [...visible].sort((a, b) => a - b)

    sorted.forEach((value, index) => {
        if (index > 0 && value - sorted[index - 1] > 1) result.push('gap')
        result.push(value)
    })

    return result
}

const Pagination: FC<PaginationProps> = ({ page, pageSize, total, basePath, params = {}, anchor }): ReactNode => {
    const pages = Math.max(1, Math.ceil(total / pageSize))

    if (pages <= 1) return null

    const itemClass = 'inline-flex h-10 min-w-10 items-center justify-center rounded-lg border-2 border-foreground px-3 text-sm font-bold transition-colors'

    return (
        <nav aria-label='Pagination' className='flex flex-wrap items-center justify-center gap-2'>
            {page > 1 ? (
                <Link href={buildHref(basePath, params, page - 1, anchor)} className={cn(itemClass, 'bg-card hover:bg-primary')}>
                    <ChevronLeft className='size-4' aria-hidden='true' />
                    <span className='ml-1 hidden sm:inline'>Previous</span>
                </Link>
            ) : null}

            {pageWindow(page, pages).map((value, index) => value === 'gap' ? (
                <span key={`gap-${index}`} className='px-1 font-mono text-sm text-muted-foreground'>
                    ...
                </span>
            ) : (
                <Link
                    key={value}
                    href={buildHref(basePath, params, value, anchor)}
                    aria-current={value === page ? 'page' : undefined}
                    className={cn(itemClass, value === page ? 'bg-foreground text-background' : 'bg-card hover:bg-primary')}
                >
                    {value}
                </Link>
            ))}

            {page < pages ? (
                <Link href={buildHref(basePath, params, page + 1, anchor)} className={cn(itemClass, 'bg-card hover:bg-primary')}>
                    <span className='mr-1 hidden sm:inline'>Next</span>
                    <ChevronRight className='size-4' aria-hidden='true' />
                </Link>
            ) : null}
        </nav>
    )
}

export default Pagination
